<div
  *ngIf="visible"
  class="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm z-[9999] p-4 transition-opacity"
  (click)="closeDialog()"
>
  <div
    class="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all transform scale-100"
    [style.width]="config.width || '70vw'"
    [style.height]="config.height || 'auto'"
    [style.max-height]="'90vh'"
    (click)="$event.stopPropagation()"
  >
    
    <div class="flex items-center justify-between px-8 py-3 border-b border-gray-200 bg-slate-200">
      <div>
        <h3 class="text-xl font-bold text-gray-800 tracking-tight">
          {{ config.title }}
        </h3>
        <p *ngIf="config.description" class="text-sm text-gray-500 mt-0.5">Please fill in the details below.</p>
      </div>
      
      <button
        *ngIf="config.closable !== false"
        type="button"
        class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-xl transition-colors"
        (click)="closeDialog()"
        aria-label="Close"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto bg-white/70 px-8 py-3 ">
      <form [formGroup]="form" class="pt-5 px-3">
        <!-- Outer Grid: always equal width for left/right -->
        <div
          class="grid gap-6"
          [ngClass]="config.formLayout === 'single-column' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'"
        >
          <ng-container *ngFor="let field of config.fields">
            <!-- Each field wrapper -->
            <div class="w-full min-w-0" [ngClass]="field.type === 'text-editor' ? 'md:col-span-2' : ''">
      
              <!-- Normal fields -->
              <div *ngIf="field.type !== 'text-editor'"
                   class="grid grid-cols-[160px,1fr] gap-3 items-start w-full min-w-0">
                
                <!-- Label (fixed width same everywhere) -->
                <div class="bg-blue-600 text-white rounded px-3 py-2 flex items-center min-h-[42px]">
                  <b class="text-sm truncate">{{ field.label }}</b>
                  <span *ngIf="isFieldRequired(field.key)" class="text-red-200 ms-1">*</span>
                </div>
      
                <!-- Control (takes remaining space) -->
                <div class="min-w-0">
                  <!-- Text / Email / Password -->
                  <input
                    *ngIf="field.type === 'text' || field.type === 'email' || field.type === 'password'"
                    [type]="field.type"
                    class="w-full rounded border-2 border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                    [formControlName]="field.key"
                    [maxlength]="field.maxLength"
                    [placeholder]="field.placeholder"
                    [readonly]="field.readonly"
                  />
      
                  <!-- Date -->
                  <input
                    *ngIf="field.type === 'date'"
                    type="date"
                    class="w-full rounded border-2 border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                    [formControlName]="field.key"
                    [placeholder]="field.placeholder"
                    [readonly]="field.readonly"
                  />
      
                  <!-- Number -->
                  <input
                    *ngIf="field.type === 'number'"
                    type="number"
                    class="w-full rounded border-2 border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                    [formControlName]="field.key"
                    [placeholder]="field.placeholder"
                    [readonly]="field.readonly"
                  />
      
                  <!-- Textarea -->
                  <textarea
                    *ngIf="field.type === 'textarea'"
                    class="w-full rounded border-2 border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                    [formControlName]="field.key"
                    [maxlength]="field.maxLength"
                    [placeholder]="field.placeholder"
                    [readonly]="field.readonly"
                    rows="3"
                  ></textarea>
      
                  <!-- Select -->
                  <select
                    *ngIf="field.type === 'select'"
                    class="w-full rounded border-2 border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                    [formControlName]="field.key"
                    [disabled]="field.disabled"
                  >
                    <option value="" disabled>Select {{ field.label }}</option>
                    <option *ngFor="let option of field.options" [value]="option.value">
                      {{ option.label }}
                    </option>
                  </select>
      
                  <!-- Radio -->
                  <div *ngIf="field.type === 'radio'" class="flex flex-wrap gap-3 pt-2">
                    <label *ngFor="let option of field.options" class="flex items-center gap-2 text-sm">
                      <input
                        class="h-4 w-4"
                        type="radio"
                        [formControlName]="field.key"
                        [value]="option.value"
                        [id]="field.key + '_' + option.value"
                      />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
      
                  <!-- Checkbox -->
                  <label *ngIf="field.type === 'checkbox'" class="flex items-center gap-2 pt-2 text-sm">
                    <input class="h-4 w-4" type="checkbox" [formControlName]="field.key" [id]="field.key" />
                    <span>{{ field.label }}</span>
                  </label>
      
                  <!-- Error -->
                  <div *ngIf="form.get(field.key)?.touched" class="mt-1">
                    <small class="text-red-600" *ngIf="getFieldError(field.key)">
                      {{ getFieldError(field.key) }}
                    </small>
                  </div>
                </div>
              </div>
      
              <!-- Text editor: full row -->
              <div *ngIf="field.type === 'text-editor'" class="w-full">
                <div class="bg-blue-600 text-white rounded px-3 py-2 flex items-center">
                  <b class="text-sm">{{ field.label }}</b>
                  <span *ngIf="isFieldRequired(field.key)" class="text-red-200 ms-1">*</span>
                </div>
      
                <div class="mt-2">
                  <small class="text-red-600" *ngIf="getFieldError(field.key)">
                    {{ getFieldError(field.key) }}
                  </small>
      
                  <div class="mt-1">
                    <editor [id]="field.key" [init]="init" [formControlName]="field.key"></editor>
                  </div>
                </div>
              </div>
      
            </div>
          </ng-container>
        </div>
      
       
      </form>
      
    </div>

    <div class="flex justify-end items-center gap-3 px-8 py-3 bg-slate-200 border-t border-gray-100">
      <button
        *ngIf="config.showCancelButton !== false"
        type="button"
        class="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-slate-300 rounded-xl transition-all"
        (click)="onCancelForm()"
      >
        {{ config.cancelButtonText || 'Cancel' }}
      </button>

      <button *ngIf="!viewMode"
        type="submit"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-200 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none rounded-xl transition-all"
        [disabled]="form.invalid"
        (click)="onSubmitForm()"
      >
        {{ config.submitButtonText || 'Save Changes' }}
      </button>
    </div>
  </div>
</div>